import { useCallback, useEffect, useId, useRef, useState } from "react";
import TRAIcon from "../icons/TRAIcon";
import TRAButton from "../components/primitives/TRAButton";
import { TRAPillButton } from "./TRAVisualPatterns";
import styles from "./traSearchWithFilterDrop.module.css";

/**
 * Canonical platform template: search bar + filter drop-down.
 *
 * Spec (mandatory): ~56px search header · query ~15px · filters drop from the bar
 * (animated expand, not a bottom sheet) · compact drop fields (~0.84rem, not bold) ·
 * removable mint chips · Clear + Apply (`TRAPillButton variant="cta"`).
 *
 * Use this whenever a list needs query + advanced filters.
 * Do **not** invent full-page filter forms or bottom-sheet filters.
 *
 * @param {{
 *   query?: string,
 *   onQueryChange?: (query: string) => void,
 *   placeholder?: string,
 *   searchAriaLabel?: string,
 *   filterAriaLabel?: string,
 *   filterActiveCount?: number,
 *   chips?: { id: string, label: string, onClear: () => void }[],
 *   children?: React.ReactNode | ((ctx: { open: boolean, close: () => void }) => React.ReactNode),
 *   onOpenChange?: (open: boolean) => void,
 *   testId?: string,
 *   className?: string,
 *   variant?: 'default' | 'compact',
 * }} props — `compact` = Arena-aligned 44px light pill (Tournaments hub).
 */
export default function TRASearchWithFilterDrop({
  query = "",
  onQueryChange,
  placeholder = "Search…",
  searchAriaLabel = "Search",
  filterAriaLabel = "Filters",
  filterActiveCount = 0,
  chips = [],
  children,
  onOpenChange,
  testId = "search-with-filter",
  className,
  variant = "default",
}) {
  const rootRef = useRef(null);
  const reactId = useId();
  const dropId = `${testId}-drop-${reactId}`;
  const [open, setOpen] = useState(false);
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  const setOpenSafe = useCallback((next) => {
    setOpen(next);
    onOpenChangeRef.current?.(next);
  }, []);

  const toggleOpen = () => setOpenSafe(!open);
  const close = useCallback(() => setOpenSafe(false), [setOpenSafe]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) close();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  const panel =
    typeof children === "function" ? children({ open, close }) : children;

  const compact = variant === "compact";

  return (
    <div
      className={`${styles.root}${compact ? ` ${styles.rootCompact}` : ""}${
        className ? ` ${className}` : ""
      }`}
      data-testid={testId}
      data-tra-search-variant={variant}
      ref={rootRef}
    >
      <div
        className={`${styles.combo}${compact ? ` ${styles.comboCompact}` : ""}${
          open ? ` ${styles.comboOpen}` : ""
        }`}
      >
        <div className={`${styles.pill}${compact ? ` ${styles.pillCompact}` : ""}`} role="search">
          <TRAIcon name="search" size="sm" className={styles.pillIcon} aria-hidden />
          <input
            className={`${styles.pillInput}${compact ? ` ${styles.pillInputCompact}` : ""}`}
            value={query}
            onChange={(e) => onQueryChange?.(e.target.value)}
            placeholder={placeholder}
            aria-label={searchAriaLabel}
            data-testid={`${testId}-input`}
          />
          <span className={styles.pillDivider} aria-hidden />
          <button
            type="button"
            className={`${styles.filterBtn}${compact ? ` ${styles.filterBtnCompact}` : ""}${
              open ? ` ${styles.filterBtnOpen}` : ""
            }`}
            aria-label={
              filterActiveCount
                ? `Filters, ${filterActiveCount} active`
                : open
                  ? "Close filters"
                  : "Open filters"
            }
            aria-expanded={open}
            aria-controls={dropId}
            data-testid={`${testId}-filter-button`}
            onClick={toggleOpen}
          >
            <TRAIcon name="sliders" size="sm" />
            {filterActiveCount > 0 ? (
              <span className={styles.badge} aria-hidden>
                {filterActiveCount}
              </span>
            ) : null}
          </button>
        </div>

        <div
          id={dropId}
          className={`${styles.drop}${open ? ` ${styles.dropOpen}` : ""}`}
          aria-hidden={!open}
        >
          <div className={styles.dropClip}>
            <div
              className={styles.dropInner}
              data-testid={`${testId}-panel`}
              role="region"
              aria-label={filterAriaLabel}
            >
              <div className={styles.dropRule} aria-hidden />
              {panel}
            </div>
          </div>
        </div>
      </div>

      {chips.length ? (
        <div className={styles.chipRow} aria-label="Active filters">
          {chips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              className={styles.chip}
              aria-label={`Clear ${chip.label}`}
              data-testid={`${testId}-chip-${chip.id}`}
              onClick={() => chip.onClear?.()}
            >
              <span>{chip.label}</span>
              <TRAIcon name="close" size="sm" className={styles.chipX} />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Standard Clear + Apply row for the drop panel. */
export function TRASearchFilterDropActions({
  onClear,
  onApply,
  clearLabel = "Clear",
  applyLabel = "Apply filters",
  disabled = false,
  open = true,
}) {
  const tabIndex = open ? 0 : -1;
  return (
    <div className={styles.actions}>
      <TRAButton
        type="button"
        variant="outlined"
        tabIndex={tabIndex}
        disabled={disabled}
        onClick={onClear}
      >
        {clearLabel}
      </TRAButton>
      <TRAPillButton
        type="button"
        variant="cta"
        tabIndex={tabIndex}
        disabled={disabled}
        onClick={onApply}
      >
        {applyLabel}
      </TRAPillButton>
    </div>
  );
}

/** Label + control field for drop-panel filters (regular weight, not bold). */
export function TRASearchFilterField({ label, children }) {
  return (
    <label className={styles.field}>
      {label}
      {children}
    </label>
  );
}

